import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { unusedPortalMember, unresolvedMemberLinks, currentMemberLabel } from "@/lib/portal-members";

export const RELATIONSHIPS = [
  "Child", "FosterChild", "Friend", "GrandChild", "GrandParent",
  "GreatGrandChild", "GreatGrandParent", "Parent", "Sibling", "Spouse",
];
export const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];

const REL_LABELS = {
  FosterChild: "Foster child", GrandChild: "Grandchild", GrandParent: "Grandparent",
  GreatGrandChild: "Great-grandchild", GreatGrandParent: "Great-grandparent",
};

const inputCls = "w-full px-3 py-2 rounded-lg border outline-none focus:border-[#052F66] text-sm disabled:opacity-60";
const inputStyle = { borderColor: "#F59E0B", background: "#FFF" };
const labelCls = "text-xs font-semibold mb-1 block";
const labelStyle = { color: "#8B918B" };

function str(v) {
  return v === null || v === undefined ? "" : String(v);
}

function SelectWithLegacy({ id, value, options, labels = {}, onChange, disabled, required = false, invalid = false }) {
  const v = str(value);
  const legacy = v !== "" && !options.includes(v);
  return (
    <select id={id} value={v} disabled={disabled} required={required} aria-invalid={invalid}
            aria-describedby={invalid ? "pending-approval-error" : undefined} onChange={(e) => onChange(e.target.value)}
            className={inputCls} style={inputStyle}>
      <option value="">{required ? "Select gender" : "Not specified"}</option>
      {legacy && <option value={v}>{v} (existing value)</option>}
      {options.map((o) => <option key={o} value={o}>{labels[o] || o}</option>)}
    </select>
  );
}

export default function PendingMemberEditor({ members, onChange, disabled = false, showGenderErrors = false, currentMembers = null }) {
  const list = Array.isArray(members) ? members : [];
  const unresolved = currentMembers === null ? [] : unresolvedMemberLinks(list, currentMembers);

  function update(i, key, value) {
    onChange(list.map((m, idx) => (idx === i ? { ...m, [key]: value } : m)));
  }
  function remove(i) {
    onChange(list.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...list, {
      first_name: "", last_name: "", gender: "", birth_year: "", birth_month: "",
      relationship: "", phone: "", email: "",
    }]);
  }

  return (
    <div className="space-y-3" data-testid="pending-member-editor">
      <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#B45309" }}>
        Household Members
      </div>

      {list.length === 0 && (
        <div className="rounded-lg border border-dashed p-4 text-sm text-center"
             style={{ borderColor: "#F59E0B", color: "var(--fb-text-muted)" }}>
          No household members in this proposal.
        </div>
      )}

      {list.map((m, i) => {
        const p = `pme-${i}`;
        const n = i + 1;
        const name = [m.first_name, m.last_name].filter(Boolean).join(" ") || `Member ${n}`;
        return (
          <fieldset key={m.id ?? `new-${i}`} className="rounded-lg border p-3 bg-white"
                    style={{ borderColor: "var(--fb-border)" }} data-testid={`pme-row-${i}`}>
            <legend className="px-1 text-xs font-semibold" style={{ color: "var(--fb-text)" }}>
              Member {n}{name !== `Member ${n}` ? `: ${name}` : ""}
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor={`${p}-first`} className={labelCls} style={labelStyle}>First name (member {n})</label>
                <input id={`${p}-first`} type="text" value={str(m.first_name)} disabled={disabled}
                       onChange={(e) => update(i, "first_name", e.target.value)} className={inputCls} style={inputStyle} />
              </div>
              <div>
                <label htmlFor={`${p}-last`} className={labelCls} style={labelStyle}>Last name (member {n})</label>
                <input id={`${p}-last`} type="text" value={str(m.last_name)} disabled={disabled}
                       onChange={(e) => update(i, "last_name", e.target.value)} className={inputCls} style={inputStyle} />
              </div>
              <div>
                <label htmlFor={`${p}-rel`} className={labelCls} style={labelStyle}>Relationship (member {n})</label>
                <SelectWithLegacy id={`${p}-rel`} value={m.relationship} options={RELATIONSHIPS} labels={REL_LABELS}
                                  disabled={disabled} onChange={(v) => update(i, "relationship", v)} />
              </div>
              <div>
                <label htmlFor={`${p}-gender`} className={labelCls} style={labelStyle}>Gender (member {n})</label>
                <SelectWithLegacy id={`${p}-gender`} value={m.gender} options={GENDERS}
                                  required={!unusedPortalMember(m)}
                                  invalid={showGenderErrors && !unusedPortalMember(m) && !GENDERS.includes(m.gender)}
                                  disabled={disabled} onChange={(v) => update(i, "gender", v)} />
              </div>
              <div>
                <label htmlFor={`${p}-age`} className={labelCls} style={labelStyle}>Age (member {n})</label>
                <input id={`${p}-age`} type="number" min="0" max="120" placeholder="Age in years"
                       value={m.birth_year && Number.isFinite(Number(m.birth_year)) ? new Date().getFullYear() - Number(m.birth_year) : ""}
                       disabled={disabled}
                       onChange={(e) => onChange(list.map((item, idx) => idx === i ? {
                         ...item,
                         birth_year: e.target.value === "" ? null : new Date().getFullYear() - Number(e.target.value),
                         birth_month: e.target.value === "" ? null :
                           (Number(item.birth_month) >= 1 && Number(item.birth_month) <= 12 ? item.birth_month : 1),
                       } : item))} className={inputCls} style={inputStyle} />
              </div>
              <div>
                <label htmlFor={`${p}-phone`} className={labelCls} style={labelStyle}>Phone, optional (member {n})</label>
                <input id={`${p}-phone`} type="tel" value={str(m.phone)} disabled={disabled}
                       onChange={(e) => update(i, "phone", e.target.value)} className={inputCls} style={inputStyle} />
              </div>
              <div>
                <label htmlFor={`${p}-email`} className={labelCls} style={labelStyle}>Email, optional (member {n})</label>
                <input id={`${p}-email`} type="email" value={str(m.email)} disabled={disabled}
                       onChange={(e) => update(i, "email", e.target.value)} className={inputCls} style={inputStyle} />
              </div>
            </div>
            {currentMembers !== null && <div className="mt-3">
              <label htmlFor={`${p}-match`} className={labelCls} style={labelStyle}>
                Current household record (member {n})
              </label>
              <select id={`${p}-match`} className={inputCls} style={inputStyle}
                      disabled={disabled} aria-invalid={unresolved.includes(i)}
                      aria-describedby={`${p}-match-help`}
                      value={currentMembers.some(saved => saved.id === m.id) ? m.id : m.id ? "" : "__new__"}
                      onChange={e => {
                        const saved = currentMembers.find(row => row.id === e.target.value);
                        if (!saved) return;
                        onChange(list.map((row, idx) => idx === i ? {
                          ...row, id: saved.id,
                          ...(!Object.prototype.hasOwnProperty.call(row, "gender") ? { gender: saved.gender } : {}),
                        } : row));
                      }}>
                {m.id ? <option value="">Select the correct current member</option> :
                  <option value="__new__">New member — not a current household record</option>}
                {currentMembers.map(saved => <option key={saved.id} value={saved.id}
                  disabled={list.some((row, idx) => idx !== i && row.id === saved.id)}>
                  {currentMemberLabel(saved)}
                </option>)}
              </select>
              <p id={`${p}-match-help`} className="text-xs mt-1" style={{ color: unresolved.includes(i) ? "var(--fb-error)" : "var(--fb-text-muted)" }}>
                {unresolved.includes(i) ? "This submitted record is no longer available or is linked twice. Select the correct current member, or keep the current household roster." :
                  "Compare these current details with the submitted fields above. Selecting a record retains its identity; it does not replace the submitted field values."}
              </p>
            </div>}
            <div className="flex justify-end mt-3">
              <button type="button" onClick={() => remove(i)} disabled={disabled}
                      aria-label={`Remove member ${n}: ${name}`} data-testid={`pme-remove-${i}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border hover:bg-rose-50 disabled:opacity-50"
                      style={{ borderColor: "var(--fb-error)", color: "var(--fb-error)" }}>
                <Trash2 className="w-3.5 h-3.5" aria-hidden="true" /> Remove
              </button>
            </div>
          </fieldset>
        );
      })}

      <button type="button" onClick={add} disabled={disabled} data-testid="pme-add"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border hover:bg-stone-50 disabled:opacity-50"
              style={{ borderColor: "var(--fb-border)", color: "var(--fb-text)", background: "#FFF" }}>
        <Plus className="w-4 h-4" aria-hidden="true" /> Add member
      </button>
    </div>
  );
}
